import type { BuildingFootprintData } from "../schema/tile.ts";
import { classifyVilniusBuildingStyle } from "./classifier.ts";
import {
  BUILDING_VISUAL_SPEC_SCHEMA_VERSION,
  type AntakalnisSuburbanBuildingVisualSpec,
  type BuildingFacadePalette,
  type BuildingFloorVisualSpec,
  type BuildingHeightVisualSpec,
  type BuildingVisualCentroids,
  type BuildingVisualGenerationOptions,
  type NaujamiestisIndustrialBuildingVisualSpec,
  type OldTownBuildingVisualSpec,
  type SovietPanelBuildingVisualSpec,
  type SnipiskesBuildingVisualSpec,
  type VilniusBuildingStyleFamily,
  type VilniusBuildingVisualSpec,
  type ZverynasBuildingVisualSpec,
} from "./types.ts";

const OLD_TOWN_PALETTES = [
  {
    id: "old_town_ochre",
    wall: "#a58c68",
    trim: "#c1b49d",
    accent: "#6f594d",
  },
  {
    id: "old_town_limestone",
    wall: "#aaa08e",
    trim: "#c5bba8",
    accent: "#71675c",
  },
  {
    id: "old_town_faded_rose",
    wall: "#9f7c72",
    trim: "#baa99a",
    accent: "#674f4b",
  },
  {
    id: "old_town_sage",
    wall: "#829083",
    trim: "#b5b4a2",
    accent: "#59665d",
  },
  {
    id: "old_town_warm_cream",
    wall: "#b8aa8d",
    trim: "#d0c4ac",
    accent: "#776859",
  },
  {
    id: "old_town_pale_yellow",
    wall: "#b2a276",
    trim: "#d0c39f",
    accent: "#74644c",
  },
] as const satisfies readonly BuildingFacadePalette[];

// The reviewed Old Town material cues show clay remaining dominant while
// grey and dark standing-seam roofs punctuate the roofscape. Repeated entries
// keep this a controlled district grammar rather than an even random mix.
const OLD_TOWN_ROOF_PALETTES = [
  "muted_clay",
  "muted_clay",
  "muted_clay",
  "muted_clay",
  "muted_clay",
  "muted_clay",
  "aged_clay",
  "aged_clay",
  "aged_clay",
  "weathered_metal",
  "weathered_metal",
  "dark_metal",
] as const;

const SOVIET_PANEL_PALETTES = [
  {
    id: "panel_weathered_concrete",
    wall: "#949692",
    trim: "#b3b1aa",
    accent: "#626967",
  },
  {
    id: "panel_warm_concrete",
    wall: "#a19a8f",
    trim: "#bbb3a8",
    accent: "#716b64",
  },
  {
    id: "panel_pale_sand",
    wall: "#aaa392",
    trim: "#c0b9ab",
    accent: "#6f716d",
  },
  {
    id: "panel_muted_blue",
    wall: "#838e92",
    trim: "#abb1b0",
    accent: "#5d6368",
  },
] as const satisfies readonly BuildingFacadePalette[];

const ZVERYNAS_PALETTES = [
  {
    id: "zverynas_sage_timber",
    wall: "#668477",
    trim: "#c3c1a8",
    accent: "#475b52",
  },
  {
    id: "zverynas_weathered_blue",
    wall: "#5f7e84",
    trim: "#c8c1aa",
    accent: "#41575d",
  },
  {
    id: "zverynas_muted_ochre",
    wall: "#9b7745",
    trim: "#d0c5a6",
    accent: "#6d4f32",
  },
  {
    id: "zverynas_pale_mint",
    wall: "#789d86",
    trim: "#d0c9ae",
    accent: "#8b4335",
  },
] as const satisfies readonly BuildingFacadePalette[];

// Medinės Šnipiškės retain plainer late-19th/early-20th-century
// suburban timber houses than the more decorative Žvėrynas villas. Keep the
// paint weathered and restrained while preserving readable trim at street level.
const SNIPISKES_PALETTES = [
  {
    id: "snipiskes_weathered_grey",
    wall: "#77766d",
    trim: "#b7b09d",
    accent: "#4e4c45",
  },
  {
    id: "snipiskes_faded_ochre",
    wall: "#8f744f",
    trim: "#c7b99c",
    accent: "#624d36",
  },
  {
    id: "snipiskes_muted_green",
    wall: "#637568",
    trim: "#b9b79f",
    accent: "#435047",
  },
  {
    id: "snipiskes_dusty_blue",
    wall: "#66767b",
    trim: "#bbb7a6",
    accent: "#465257",
  },
] as const satisfies readonly BuildingFacadePalette[];

// The Panerių factory belt is defined by long, economical masonry volumes:
// dark red and brown brick, pale repaired lintels, and tall vertical glazing.
// Keep the palette muted so these buildings remain part of the miniature city
// while reading distinctly from both Old Town plaster and panel housing.
const NAUJAMIESTIS_INDUSTRIAL_PALETTES = [
  {
    id: "naujamiestis_dark_red_brick",
    wall: "#79564b",
    trim: "#a99782",
    accent: "#4f423c",
  },
  {
    id: "naujamiestis_weathered_brown_brick",
    wall: "#826759",
    trim: "#b3a48e",
    accent: "#554840",
  },
  {
    id: "naujamiestis_soot_washed_brick",
    wall: "#6d5b53",
    trim: "#9f9585",
    accent: "#41413f",
  },
] as const satisfies readonly BuildingFacadePalette[];

// The Antakalnio g. 26B conservation material documents a mixed suburban
// fabric: modest painted timber houses beside rendered masonry villas. Keep
// both surfaces in one restrained grammar rather than turning the pocket into
// a second Žvėrynas.
const ANTAKALNIS_TIMBER_PALETTES = [
  {
    id: "antakalnis_faded_sage_timber",
    wall: "#748076",
    trim: "#c1baa7",
    accent: "#4c5851",
  },
  {
    id: "antakalnis_weathered_ochre_timber",
    wall: "#927758",
    trim: "#cabda5",
    accent: "#604d3d",
  },
] as const satisfies readonly BuildingFacadePalette[];

const ANTAKALNIS_PLASTER_PALETTES = [
  {
    id: "antakalnis_pale_cream_plaster",
    wall: "#b4a98e",
    trim: "#d1c8b4",
    accent: "#756b5e",
  },
  {
    id: "antakalnis_muted_rose_plaster",
    wall: "#a4867b",
    trim: "#c9b8a8",
    accent: "#6b554f",
  },
] as const satisfies readonly BuildingFacadePalette[];

const OLD_TOWN_FALLBACK_FLOORS = [2, 3, 3, 3, 4] as const;
// The authored M4 reference documents a deliberate 5/9/12-storey mix in
// Lithuanian panel-housing ensembles. Keep five-storey slabs dominant while
// allowing the benchmark skyline to express the two taller archetypes.
const SOVIET_PANEL_FALLBACK_FLOORS = [5, 5, 5, 9, 9, 12] as const;
const ZVERYNAS_FALLBACK_FLOORS = [1, 1, 1, 2, 2] as const;
const SNIPISKES_FALLBACK_FLOORS = [1, 1, 1, 1, 2] as const;
const NAUJAMIESTIS_INDUSTRIAL_FALLBACK_FLOORS = [2, 2, 3, 3, 4] as const;
const ANTAKALNIS_SUBURBAN_FALLBACK_FLOORS = [1, 1, 1, 1, 2] as const;
const OLD_TOWN_FLOOR_HEIGHT_M = 3.2;
const OLD_TOWN_ROOF_ALLOWANCE_M = 2.2;
const SOVIET_PANEL_FLOOR_HEIGHT_M = 2.85;
const SOVIET_PANEL_PARAPET_ALLOWANCE_M = 0.6;
const ZVERYNAS_FLOOR_HEIGHT_M = 3.05;
const ZVERYNAS_ROOF_ALLOWANCE_M = 3.0;
const SNIPISKES_FLOOR_HEIGHT_M = 2.95;
const SNIPISKES_ROOF_ALLOWANCE_M = 2.15;
const NAUJAMIESTIS_INDUSTRIAL_FLOOR_HEIGHT_M = 3.6;
const NAUJAMIESTIS_INDUSTRIAL_PARAPET_ALLOWANCE_M = 0.7;
const ANTAKALNIS_SUBURBAN_FLOOR_HEIGHT_M = 3.05;
const ANTAKALNIS_SUBURBAN_ROOF_ALLOWANCE_M = 2.5;

export function stableBuildingSeed(buildingId: string): number {
  assertNonEmpty(buildingId, "Building ID");
  let hash = 0x811c9dc5;
  for (let index = 0; index < buildingId.length; index += 1) {
    hash ^= buildingId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export function generateVilniusBuildingVisualSpec(
  building: Readonly<BuildingFootprintData>,
  centroids: Readonly<BuildingVisualCentroids>,
  options: Readonly<BuildingVisualGenerationOptions> = {},
): VilniusBuildingVisualSpec {
  validateInput(building, centroids);
  const styleFamily = classifyVilniusBuildingStyle(
    centroids.global,
    options,
  );
  const seed = stableBuildingSeed(building.id);
  const dimensions = dimensionsFor(building, styleFamily, seed);
  const longestFootprintSpanM = footprintLongestSpanM(building);
  const common = {
    schemaVersion: BUILDING_VISUAL_SPEC_SCHEMA_VERSION,
    buildingId: building.id,
    sourceFootprintId: building.sourceFootprintId,
    sourceId: building.sourceId,
    seed,
    baseElevationM: building.baseElevationM,
    centroids: {
      tile: { ...centroids.tile },
      global: { ...centroids.global },
    },
    height: dimensions.height,
    floors: dimensions.floors,
    ...(building.roofOrientation === undefined
      ? {}
      : { roofOrientation: building.roofOrientation }),
    ...(building.roofRiseM === undefined
      ? {}
      : { roofRiseM: building.roofRiseM }),
  } as const;

  if (styleFamily === "old_town") {
    return oldTownVisualSpec(common, longestFootprintSpanM, building.roofShape);
  }
  if (styleFamily === "antakalnis_suburban") {
    return antakalnisSuburbanVisualSpec(
      common,
      longestFootprintSpanM,
      building.roofShape,
    );
  }
  if (styleFamily === "naujamiestis_industrial") {
    return naujamiestisIndustrialVisualSpec(
      common,
      longestFootprintSpanM,
      building.roofShape,
    );
  }
  if (styleFamily === "zverynas") {
    return zverynasVisualSpec(common, longestFootprintSpanM, building.roofShape);
  }
  if (styleFamily === "snipiskes") {
    return snipiskesVisualSpec(common, longestFootprintSpanM, building.roofShape);
  }
  return sovietPanelVisualSpec(common, longestFootprintSpanM, building.roofShape);
}

interface VisualDimensions {
  height: BuildingHeightVisualSpec;
  floors: BuildingFloorVisualSpec;
}

function dimensionsFor(
  building: Readonly<BuildingFootprintData>,
  family: VilniusBuildingStyleFamily,
  seed: number,
): VisualDimensions {
  if (building.heightM !== undefined) {
    const typicalFloorHeightM = family === "old_town"
      ? OLD_TOWN_FLOOR_HEIGHT_M
      : family === "antakalnis_suburban"
        ? ANTAKALNIS_SUBURBAN_FLOOR_HEIGHT_M
      : family === "zverynas"
        ? ZVERYNAS_FLOOR_HEIGHT_M
        : family === "snipiskes"
          ? SNIPISKES_FLOOR_HEIGHT_M
          : family === "naujamiestis_industrial"
            ? NAUJAMIESTIS_INDUSTRIAL_FLOOR_HEIGHT_M
        : SOVIET_PANEL_FLOOR_HEIGHT_M;
    return {
      height: {
        metres: building.heightM,
        confidence: "source",
      },
      floors: {
        count: Math.max(1, Math.round(building.heightM / typicalFloorHeightM)),
        confidence: "derived_from_source_height",
      },
    };
  }

  const floors = family === "old_town"
    ? select(OLD_TOWN_FALLBACK_FLOORS, seed, "fallback-floors")
    : family === "antakalnis_suburban"
      ? select(ANTAKALNIS_SUBURBAN_FALLBACK_FLOORS, seed, "fallback-floors")
    : family === "zverynas"
      ? select(ZVERYNAS_FALLBACK_FLOORS, seed, "fallback-floors")
      : family === "snipiskes"
        ? select(SNIPISKES_FALLBACK_FLOORS, seed, "fallback-floors")
        : family === "naujamiestis_industrial"
          ? select(
              NAUJAMIESTIS_INDUSTRIAL_FALLBACK_FLOORS,
              seed,
              "fallback-floors",
            )
      : select(SOVIET_PANEL_FALLBACK_FLOORS, seed, "fallback-floors");
  return {
    height: {
      metres: family === "old_town"
        ? roundToCentimetres(
            floors * OLD_TOWN_FLOOR_HEIGHT_M +
              OLD_TOWN_ROOF_ALLOWANCE_M,
          )
        : family === "antakalnis_suburban"
          ? roundToCentimetres(
              floors * ANTAKALNIS_SUBURBAN_FLOOR_HEIGHT_M +
                ANTAKALNIS_SUBURBAN_ROOF_ALLOWANCE_M,
            )
        : family === "zverynas"
          ? roundToCentimetres(
              floors * ZVERYNAS_FLOOR_HEIGHT_M +
                ZVERYNAS_ROOF_ALLOWANCE_M,
            )
          : family === "snipiskes"
            ? roundToCentimetres(
                floors * SNIPISKES_FLOOR_HEIGHT_M +
                  SNIPISKES_ROOF_ALLOWANCE_M,
              )
            : family === "naujamiestis_industrial"
              ? roundToCentimetres(
                  floors * NAUJAMIESTIS_INDUSTRIAL_FLOOR_HEIGHT_M +
                    NAUJAMIESTIS_INDUSTRIAL_PARAPET_ALLOWANCE_M,
                )
        : roundToCentimetres(
            floors * SOVIET_PANEL_FLOOR_HEIGHT_M +
              SOVIET_PANEL_PARAPET_ALLOWANCE_M,
          ),
      confidence: "district_fallback",
    },
    floors: {
      count: floors,
      confidence: "district_fallback",
    },
  };
}

type CommonVisualSpec = Pick<
  OldTownBuildingVisualSpec,
  | "schemaVersion"
  | "buildingId"
  | "sourceFootprintId"
  | "sourceId"
  | "seed"
  | "baseElevationM"
  | "centroids"
  | "height"
  | "floors"
>;

function antakalnisSuburbanVisualSpec(
  common: CommonVisualSpec,
  longestFootprintSpanM: number,
  sourceRoofShape: BuildingFootprintData["roofShape"],
): AntakalnisSuburbanBuildingVisualSpec {
  const surface = variant(common.seed, "antakalnis-surface", 5) < 3
    ? "painted_timber"
    : "plaster";
  const palette = surface === "painted_timber"
    ? select(
        ANTAKALNIS_TIMBER_PALETTES,
        common.seed,
        "antakalnis-timber-palette",
      )
    : select(
        ANTAKALNIS_PLASTER_PALETTES,
        common.seed,
        "antakalnis-plaster-palette",
      );
  const fallbackRoofType = variant(common.seed, "antakalnis-roof-type", 4) === 0
    ? "hip"
    : "gable";
  const roofType = sourceRoofShape === "gable" || sourceRoofShape === "hip"
    ? sourceRoofShape
    : fallbackRoofType;
  return {
    ...common,
    styleFamily: "antakalnis_suburban",
    facade: {
      family: "antakalnis_suburban_villa",
      surface,
      palette: { ...palette },
      bayCount: clampInteger(
        Math.round(longestFootprintSpanM / 3.6) +
          variant(common.seed, "antakalnis-bay-jitter", 3) - 1,
        2,
        9,
      ),
      windowRhythm: {
        kind: variant(common.seed, "antakalnis-window-rhythm", 4) === 0
          ? "old_town_paired"
          : "old_town_vertical",
        rowCount: common.floors.count,
        columnsPerBay:
          variant(common.seed, "antakalnis-window-rhythm", 4) === 0 ? 2 : 1,
        windowWidthToBayRatio:
          0.4 + variant(common.seed, "antakalnis-window-width", 5) * 0.017,
        windowHeightToFloorRatio:
          0.49 + variant(common.seed, "antakalnis-window-height", 4) * 0.018,
      },
    },
    roof: {
      type: roofType,
      pitchDegrees: 36 + variant(common.seed, "antakalnis-roof-pitch", 9),
      confidence: roofType === sourceRoofShape ? "source" : "district_fallback",
      paletteId: select(
        ["weathered_metal", "weathered_metal", "dark_metal", "muted_clay"] as const,
        common.seed,
        "antakalnis-roof-palette",
      ),
    },
  };
}

function oldTownVisualSpec(
  common: CommonVisualSpec,
  longestFootprintSpanM: number,
  sourceRoofShape: BuildingFootprintData["roofShape"],
): OldTownBuildingVisualSpec {
  const bayJitter = variant(common.seed, "bay-jitter", 3) - 1;
  const bayCount = clampInteger(
    Math.round(longestFootprintSpanM / 3.7) + bayJitter,
    2,
    14,
  );
  const fallbackRoofType = select(
    ["gable", "gable", "hip", "mansard"] as const,
    common.seed,
    "roof-type",
  );
  const roofType = sourceRoofShape === "gable" ||
      sourceRoofShape === "hip" || sourceRoofShape === "mansard"
    ? sourceRoofShape
    : fallbackRoofType;
  return {
    ...common,
    styleFamily: "old_town",
    facade: {
      family: "old_town_plaster",
      palette: {
        ...select(OLD_TOWN_PALETTES, common.seed, "facade-palette"),
      },
      bayCount,
      windowRhythm: {
        kind: variant(common.seed, "window-rhythm", 3) === 0
          ? "old_town_paired"
          : "old_town_vertical",
        rowCount: common.floors.count,
        columnsPerBay:
          variant(common.seed, "window-rhythm", 3) === 0 ? 2 : 1,
        windowWidthToBayRatio:
          0.42 + variant(common.seed, "window-width", 5) * 0.015,
        windowHeightToFloorRatio:
          0.48 + variant(common.seed, "window-height", 5) * 0.015,
      },
    },
    roof: {
      type: roofType,
      pitchDegrees: roofType === "mansard"
        ? 48 + variant(common.seed, "roof-pitch", 5)
        : 34 + variant(common.seed, "roof-pitch", 9),
      confidence: roofType === sourceRoofShape ? "source" : "district_fallback",
      paletteId: select(
        OLD_TOWN_ROOF_PALETTES,
        common.seed,
        "roof-palette",
      ),
    },
  };
}

function sovietPanelVisualSpec(
  common: CommonVisualSpec,
  longestFootprintSpanM: number,
  sourceRoofShape: BuildingFootprintData["roofShape"],
): SovietPanelBuildingVisualSpec {
  const bayJitter = variant(common.seed, "panel-bay-jitter", 3) - 1;
  const visualArchetype = common.floors.count <= 5
    ? "five_storey_slab"
    : common.floors.count <= 10
      ? "nine_storey_slab"
      : "tall_residential_accent";
  const balconyTreatment = visualArchetype === "five_storey_slab"
    ? variant(common.seed, "panel-balcony-treatment", 4) === 0
      ? "enclosed_loggia"
      : "projecting_solid"
    : visualArchetype === "nine_storey_slab"
      ? variant(common.seed, "panel-balcony-treatment", 3) === 0
        ? "recessed_loggia"
        : "enclosed_loggia"
      : "recessed_loggia";
  return {
    ...common,
    styleFamily: "soviet_panel",
    facade: {
      family: "soviet_panel_system",
      palette: {
        ...select(
          SOVIET_PANEL_PALETTES,
          common.seed,
          "facade-palette",
        ),
      },
      visualArchetype,
      balconyTreatment,
      entranceTreatment: "recessed_with_flat_canopy",
      baseTreatment: "dark_plinth",
      bayCount: clampInteger(
        Math.round(longestFootprintSpanM / 3.2) + bayJitter,
        3,
        24,
      ),
      windowRhythm: {
        kind: "panel_grid_with_loggias",
        rowCount: common.floors.count,
        columnsPerBay: 1,
        windowWidthToBayRatio:
          0.5 + variant(common.seed, "window-width", 4) * 0.02,
        windowHeightToFloorRatio:
          0.46 + variant(common.seed, "window-height", 4) * 0.02,
        loggiaEveryBays:
          variant(common.seed, "loggia-rhythm", 2) === 0 ? 2 : 3,
        endWalls: "mostly_blank",
      },
    },
    roof: {
      type: "flat",
      pitchDegrees: 0,
      confidence: sourceRoofShape === "flat" ? "source" : "district_fallback",
      paletteId: "concrete_cap",
    },
  };
}

function naujamiestisIndustrialVisualSpec(
  common: CommonVisualSpec,
  longestFootprintSpanM: number,
  sourceRoofShape: BuildingFootprintData["roofShape"],
): NaujamiestisIndustrialBuildingVisualSpec {
  const bayJitter = variant(common.seed, "industrial-bay-jitter", 3) - 1;
  return {
    ...common,
    styleFamily: "naujamiestis_industrial",
    facade: {
      family: "naujamiestis_brick_industrial",
      palette: {
        ...select(
          NAUJAMIESTIS_INDUSTRIAL_PALETTES,
          common.seed,
          "industrial-facade-palette",
        ),
      },
      bayCount: clampInteger(
        Math.round(longestFootprintSpanM / 4.6) + bayJitter,
        4,
        24,
      ),
      windowRhythm: {
        kind: "old_town_vertical",
        rowCount: common.floors.count,
        columnsPerBay: 1,
        windowWidthToBayRatio:
          0.5 + variant(common.seed, "industrial-window-width", 4) * 0.025,
        windowHeightToFloorRatio:
          0.58 + variant(common.seed, "industrial-window-height", 4) * 0.025,
      },
    },
    roof: {
      type: "flat",
      pitchDegrees: 0,
      confidence: sourceRoofShape === "flat" ? "source" : "district_fallback",
      paletteId: "concrete_cap",
    },
  };
}

function zverynasVisualSpec(
  common: CommonVisualSpec,
  longestFootprintSpanM: number,
  sourceRoofShape: BuildingFootprintData["roofShape"],
): ZverynasBuildingVisualSpec {
  const bayJitter = variant(common.seed, "zverynas-bay-jitter", 3) - 1;
  const fallbackRoofType = variant(common.seed, "zverynas-roof-type", 4) === 0
    ? "hip"
    : "gable";
  const roofType = sourceRoofShape === "gable" || sourceRoofShape === "hip"
    ? sourceRoofShape
    : fallbackRoofType;
  return {
    ...common,
    styleFamily: "zverynas",
    facade: {
      family: "zverynas_painted_timber",
      palette: {
        ...select(
          ZVERYNAS_PALETTES,
          common.seed,
          "zverynas-facade-palette",
        ),
      },
      bayCount: clampInteger(
        Math.round(longestFootprintSpanM / 3.4) + bayJitter,
        2,
        10,
      ),
      windowRhythm: {
        kind: variant(common.seed, "zverynas-window-rhythm", 3) === 0
          ? "old_town_paired"
          : "old_town_vertical",
        rowCount: common.floors.count,
        columnsPerBay:
          variant(common.seed, "zverynas-window-rhythm", 3) === 0 ? 2 : 1,
        windowWidthToBayRatio:
          0.40 + variant(common.seed, "zverynas-window-width", 5) * 0.018,
        windowHeightToFloorRatio:
          0.50 + variant(common.seed, "zverynas-window-height", 4) * 0.018,
      },
    },
    roof: {
      type: roofType,
      pitchDegrees: 45 + variant(common.seed, "zverynas-roof-pitch", 10),
      confidence: roofType === sourceRoofShape ? "source" : "district_fallback",
      paletteId: select(
        ["weathered_metal", "dark_metal", "muted_clay"] as const,
        common.seed,
        "zverynas-roof-palette",
      ),
    },
  };
}

function snipiskesVisualSpec(
  common: CommonVisualSpec,
  longestFootprintSpanM: number,
  sourceRoofShape: BuildingFootprintData["roofShape"],
): SnipiskesBuildingVisualSpec {
  const bayJitter = variant(common.seed, "snipiskes-bay-jitter", 3) - 1;
  const fallbackRoofType = variant(common.seed, "snipiskes-roof-type", 7) === 0
    ? "hip"
    : "gable";
  const roofType = sourceRoofShape === "gable" || sourceRoofShape === "hip"
    ? sourceRoofShape
    : fallbackRoofType;
  return {
    ...common,
    styleFamily: "snipiskes",
    facade: {
      family: "snipiskes_weathered_timber",
      palette: {
        ...select(
          SNIPISKES_PALETTES,
          common.seed,
          "snipiskes-facade-palette",
        ),
      },
      bayCount: clampInteger(
        Math.round(longestFootprintSpanM / 3.55) + bayJitter,
        2,
        8,
      ),
      windowRhythm: {
        kind: variant(common.seed, "snipiskes-window-rhythm", 4) === 0
          ? "old_town_paired"
          : "old_town_vertical",
        rowCount: common.floors.count,
        columnsPerBay:
          variant(common.seed, "snipiskes-window-rhythm", 4) === 0 ? 2 : 1,
        windowWidthToBayRatio:
          0.38 + variant(common.seed, "snipiskes-window-width", 5) * 0.016,
        windowHeightToFloorRatio:
          0.47 + variant(common.seed, "snipiskes-window-height", 4) * 0.016,
      },
    },
    roof: {
      type: roofType,
      // East-Lithuanian suburban houses in the protected core use visibly
      // lower gables than the ornate villa silhouettes west of the Neris.
      pitchDegrees: 30 + variant(common.seed, "snipiskes-roof-pitch", 9),
      confidence: roofType === sourceRoofShape ? "source" : "district_fallback",
      paletteId: select(
        [
          "weathered_metal",
          "weathered_metal",
          "dark_metal",
          "muted_clay",
        ] as const,
        common.seed,
        "snipiskes-roof-palette",
      ),
    },
  };
}

function footprintLongestSpanM(
  building: Readonly<BuildingFootprintData>,
): number {
  const points = building.footprint.outer;
  if (points.length < 3) {
    throw new Error("Building footprint needs at least three points");
  }
  let minX = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.z)) {
      throw new Error("Building footprint coordinates must be finite");
    }
    minX = Math.min(minX, point.x);
    minZ = Math.min(minZ, point.z);
    maxX = Math.max(maxX, point.x);
    maxZ = Math.max(maxZ, point.z);
  }
  const longest = Math.max(maxX - minX, maxZ - minZ);
  if (longest <= 0) {
    throw new Error("Building footprint must have positive extent");
  }
  return longest;
}

function variant(seed: number, label: string, count: number): number {
  let value = (seed ^ stableBuildingSeed(label)) >>> 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x7feb352d);
  value ^= value >>> 15;
  value = Math.imul(value, 0x846ca68b);
  value ^= value >>> 16;
  return (value >>> 0) % count;
}

function select<const T>(
  values: readonly T[],
  seed: number,
  label: string,
): T {
  const selected = values[variant(seed, label, values.length)];
  if (selected === undefined) {
    throw new Error("Building visual variant set must not be empty");
  }
  return selected;
}

function roundToCentimetres(value: number): number {
  return Math.round(value * 100) / 100;
}

function clampInteger(
  value: number,
  minimum: number,
  maximum: number,
): number {
  return Math.min(maximum, Math.max(minimum, Math.round(value)));
}

function validateInput(
  building: Readonly<BuildingFootprintData>,
  centroids: Readonly<BuildingVisualCentroids>,
): void {
  assertNonEmpty(building.id, "Building ID");
  assertNonEmpty(building.sourceFootprintId, "Source footprint ID");
  assertNonEmpty(building.sourceId, "Source ID");
  if (!Number.isFinite(building.baseElevationM)) {
    throw new Error("Building base elevation must be finite");
  }
  if (
    building.heightM !== undefined &&
    (!Number.isFinite(building.heightM) || building.heightM <= 0)
  ) {
    throw new Error("Source building height must be positive and finite");
  }
  for (const [label, point] of [
    ["tile", centroids.tile],
    ["global", centroids.global],
  ] as const) {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.z)) {
      throw new Error(`Building ${label} centroid must be finite`);
    }
  }
}

function assertNonEmpty(value: string, label: string): void {
  if (value.trim().length === 0) {
    throw new Error(`${label} must be non-empty`);
  }
}
